import { create } from 'zustand';
import { notificationService } from '../services/notification-service';
import type { Notification } from '../types/notification.types';

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  pollingIntervalId: ReturnType<typeof setInterval> | null;

  fetchNotifications: () => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  startPolling: () => void;
  stopPolling: () => void;
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  pollingIntervalId: null,

  fetchNotifications: async () => {
    try {
      set({ isLoading: true });
      const response = await notificationService.getNotifications({ limit: 20 });
      set({ notifications: response.data, isLoading: false });
    } catch {
      set({ isLoading: false });
    }
  },

  fetchUnreadCount: async () => {
    try {
      const response = await notificationService.getUnreadCount();
      set({ unreadCount: response.data.count });
    } catch {
      // silently fail
    }
  },

  markAsRead: async (id: string) => {
    // Optimistic update
    set((state) => ({
      notifications: state.notifications.map((n) =>
        n.id === id ? { ...n, isRead: true, readAt: new Date().toISOString() } : n
      ),
      unreadCount: Math.max(0, state.unreadCount - 1),
    }));

    try {
      await notificationService.markAsRead(id);
    } catch {
      // Revert on error
      get().fetchNotifications();
      get().fetchUnreadCount();
    }
  },

  markAllAsRead: async () => {
    // Optimistic update
    set((state) => ({
      notifications: state.notifications.map((n) => ({
        ...n,
        isRead: true,
        readAt: n.readAt || new Date().toISOString(),
      })),
      unreadCount: 0,
    }));

    try {
      await notificationService.markAllAsRead();
    } catch {
      get().fetchNotifications();
      get().fetchUnreadCount();
    }
  },

  deleteNotification: async (id: string) => {
    const prev = get().notifications;
    const target = prev.find((n) => n.id === id);

    // Optimistic update
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
      unreadCount:
        target && !target.isRead ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
    }));

    try {
      await notificationService.deleteNotification(id);
    } catch {
      set({ notifications: prev });
      get().fetchUnreadCount();
    }
  },

  startPolling: () => {
    const { pollingIntervalId } = get();
    if (pollingIntervalId) return; // already polling

    // Fetch immediately
    get().fetchUnreadCount();

    const id = setInterval(() => {
      get().fetchUnreadCount();
    }, 30_000);

    set({ pollingIntervalId: id });
  },

  stopPolling: () => {
    const { pollingIntervalId } = get();
    if (pollingIntervalId) {
      clearInterval(pollingIntervalId);
      set({ pollingIntervalId: null });
    }
  },
}));
